import React from 'react';
import fr from 'facet-rest';
import {
  FDBodyText,
  FDClickableText,
  FDLozenge,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import { dataAttrs, testAttr } from 'Helpers/Testing';
import { useTheme } from '@material-ui/core/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import useUserContext from 'Context/UserContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useSelectScenarioContext
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/SelectScenarioContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import {
  useRoadmapExecutiveSummaryScenarioSelection,
} from 'Views/Page/Content/Templates/RoadmapExecutiveSummaryTemplate/useRoadmapExecutiveSummaryScenarioSelection';
import SelectionConfirmationDialog
  from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedDecisionStepTemplate/Content/SelectionConfirmationDialog';
import { dashboardUriWithActionItemsListAnchor } from 'Consts/clientUris';
import useMobileLayout from 'Hooks/useMobileLayout';
import { navigateToPageInNewTab } from 'Views/Page/Content/Templates';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';

export const RoadmapExecutiveSummaryActions = ({ portfolioRel = null, isRoadmapDeliverable = false, setLoading = null }) => {
  const { resource } = useMetaLayoutContext();
  const theme = useTheme();
  const [loading, _setLoading] = React.useState(false);
  const isMobile = useMobileLayout();
  const {
    onSelectScenarioClick, selectScenario, confirmationDialogResource, setConfirmationDialogResource,
  } = useRoadmapExecutiveSummaryScenarioSelection({ resource });

  const { onAgentReview } = useSelectScenarioContext();
  const { loadPageContentLink } = usePageContentContext();
  const { isAgent } = useUserContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const selectScenarioRel = resource.withRelationship(
    fr.relationships.selectScenario.name,
    (rel) => rel,
    () => null,
  );

  const reviewScenarioRel = resource.withRelationship(
    fr.relationships.reviewScenario.name,
    (rel) => rel,
    () => null,
  );

  const actionsListRel = resource.withRelationship(
    fr.relationships.actionsList.name,
    (rel) => rel,
    () => null,
  );

  const onReviewScenarioClick = async () => {
    _setLoading(true);
    // This review section is displayed to both the client within page context and to the agent not within page context.
    // Therefore, we need to pass this function as a prop so we can change which component we show to the agent.
    if (isAgent) {
      await onAgentReview(resource);
      return;
    }

    logStandardizedEvent(categories.clientWhatIf, eventTypes.clickButton, 'Click Review on Scenario', { eventProperties: { title: resource.attr('title') } });
    await loadPageContentLink(reviewScenarioRel.link('related'));
    // We intentionally do not set loading to false because this operation will navigate us away from the page
    // and we should not attempt to update state on an unmounted component
  };

  const onUnvalidatedScenarioRel = resource.withRelationship(
    fr.relationships.unvalidatedScenario.name,
    (rel) => rel,
    () => null,
  );

  const onUnvalidatedScenarioClick = async (rel) => {
    _setLoading(true);
    navigateToPageInNewTab(
      rel.link('related').url(),
      isAgent,
    );
    // We intentionally do not set loading to false because this operation will navigate us away from the page
    // and we should not attempt to update state on an unmounted component
  };

  const onPortfolioClick = async (rel) => {
    if (setLoading) {
      setLoading(true);
    }
    _setLoading(true);

    loadPageContentLink(rel.link('related'));
  };

  const hasModelUpdates = !!resource.attr('subtitle');
  const hasButtons = !!selectScenarioRel || !!actionsListRel || !!reviewScenarioRel || !!onUnvalidatedScenarioRel || !!portfolioRel;
  if (!hasButtons && !hasModelUpdates) {
    return null;
  }

  const titleInfo = (
    <FDStack direction={'column'} spacing={2}>
      <MetaControlField attr={'title'} />
      <Richtext
        Component={FDBodyText}
        richtext={resource.meta().field('subtitle').attr('hint') === 'richtext'}
        value={resource.attr('subtitle')}
      />
      {!isRoadmapDeliverable && (<MetaControlField attr={'disclaimerText'} />)}
    </FDStack>
  );

  const actionButtons = (
    <FDStack direction={'row'} spacing={isMobile ? 2 : 3} justifyContent={'center'} wrap>
      {selectScenarioRel ? (
        <FDPrimaryActionButton
          label={selectScenarioRel.meta('prompt')}
          onClick={() => onSelectScenarioClick(selectScenarioRel)}
          loading={loading}
          {...dataAttrs('select-scenario-button', resource)}
        />
      ) : null}
      {actionsListRel ? (
        <FDPrimaryActionButton
          label={actionsListRel.meta('prompt')}
          href={dashboardUriWithActionItemsListAnchor}
          loading={loading}
          {...dataAttrs('actions-list-button', resource)}
        />
      ) : null}
      {reviewScenarioRel ? (
        <FDSecondaryActionButton
          label={reviewScenarioRel.meta('prompt')}
          onClick={onReviewScenarioClick}
          loading={loading}
          {...dataAttrs('review-scenario-button', resource)}
        />
      ) : null}
      {onUnvalidatedScenarioRel ? (
        <FDStack direction={'column'} spacing={1} wrap={isMobile}>
          <FDStack direction={'row'} spacing={1} wrap={isMobile}>
            {resource.attr('infoSnippets')?.resources().list().map((infoSnippet) => (
              <FDLozenge
                label={infoSnippet.attr('name')}
                color={infoSnippet.attr('status')}
                alignSelf={'center'}
                {...dataAttrs('scenario-preview-lozenge', infoSnippet, {
                  [testAttr.dataType]: infoSnippet.attr('status'),
                })}
              />
            ))}
          </FDStack>
          <FDPrimaryActionButton
            label={onUnvalidatedScenarioRel.meta('prompt')}
            onClick={() => onUnvalidatedScenarioClick(onUnvalidatedScenarioRel)}
            loading={loading}
            {...dataAttrs('unvalidated-scenario-button', resource)}
          />
        </FDStack>
      ) : null}
      <SelectionConfirmationDialog
        onSelectScenario={selectScenario}
        confirmationDialogResource={confirmationDialogResource}
        setConfirmationDialogResource={setConfirmationDialogResource}
      />
    </FDStack>
  );

  return (
    <FDStack direction={'column'} spacing={4}>
      <hr style={{ border: `1px solid ${theme.palette.facet.colorSunset500}`, width: '100%' }} data-test={'roadmap-summary-actions-separator'} />
      {hasModelUpdates ? (
        <FDStack direction={'column'} spacing={4}>
          {titleInfo}
          {actionButtons}
        </FDStack>
      ) : actionButtons}
      {portfolioRel ? (
        <FDStack direction={'row'}>
          <FDClickableText
            label={portfolioRel.meta('prompt')}
            appearance={'standalone'}
            onClick={() => onPortfolioClick(portfolioRel)}
            loading={loading}
            disabled={loading}
            {...dataAttrs('review-portfolio-button', resource)}
          />
        </FDStack>
      ) : null}
    </FDStack>
  );
};
